import { useState, useEffect, useCallback, useMemo } from 'react'
import Downshift from 'downshift'
import debounce from 'lodash.debounce'

import config from 'config'
import { list as listStaff } from 'modules/Staff/services/api'
import { ListResult, ReadResult } from 'interfaces/queryOptions'
import { StaffMember } from 'models'

interface Props {
  onSelect: (value: ReadResult<StaffMember> | undefined) => void
  value?: ReadResult<StaffMember>
}

export function StaffSearch({ onSelect, value }: Props) {
  const [isLoading, setIsLoading] = useState(false)
  const [search, setSearch] = useState('')

  const [staffMembers, setStaffMembers] = useState<ListResult<StaffMember>>()
  const [staff, setStaff] = useState<ReadResult<StaffMember> | undefined>(value)

  const onSearch = useMemo(
    () =>
      debounce(async (search: string) => {
        if (search.length < config.min_search_length) return

        setIsLoading(true)
        try {
          const result = await listStaff({
            attributes: ['id', 'fullname', 'email'],
            search,
            limit: 10,
            sort: { fullname: 1 }
          })

          setStaffMembers(result)
          setIsLoading(false)
        } catch (err: any) {
          setIsLoading(false)
          console.error(err)
        }
      }, config.autocomplete_debounce_rate),
    []
  )

  useEffect(() => {
    setStaff(value)
    setSearch(value?.attributes.fullname ?? '')
  }, [value])

  useEffect(() => {
    if (search === '') return
    if (search === staff?.attributes.fullname) return

    onSearch(search)
  }, [search, staff, onSearch])

  const onClick = useCallback(
    (index: number) => {
      if (!staffMembers) return

      const selected = staffMembers.data[index]
      onSelect(selected)
      setStaff(selected)
      setSearch(selected.attributes.fullname ?? '')
    },
    [staffMembers, onSelect]
  )

  return (
    <div>
      <Downshift
        onChange={() => undefined}
        itemToString={(item) => item?.attributes.fullname ?? ''}
        selectedItem={staff || { attributes: { fullname: '' } }}
      >
        {({ getInputProps, getItemProps, isOpen }) => (
          <div className="relative">
            <label className="block text-left text-sm font-medium text-gray-700">
              Responsável
            </label>
            <div className="relative flex items-center">
              <input
                className="mt-1 block w-full rounded-md shadow-sm py-2 px-3 border border-gray-300 focus:outline-none focus:border-emerald-500"
                {...getInputProps({
                  value: search || '',
                  onChange: (e) => {
                    setSearch(e.currentTarget.value)
                  }
                })}
              />
              {isLoading && (
                <div className="absolute right-1">
                  <svg
                    className="animate-spin mt-1 mr-3 h-5 w-5 text-emerald-700"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
              )}
            </div>
            <ul className="w-full absolute mt-2 rounded-md bg-white z-20 shadow flex flex-col">
              {isOpen && staffMembers && staffMembers.data.length > 0 && (
                <div className="max-h-96 overflow-auto">
                  {staffMembers.data.map((item, index) => (
                    <div
                      className="px-4 py-2 w-full cursor-pointer bg-white hover:bg-gray-100"
                      key={item.id}
                      {...getItemProps({
                        key: item.id,
                        onClick: () => {
                          onClick(index)
                        },
                        item
                      })}
                    >
                      <p className="text-md text-left text-gray-900">
                        {item.attributes.fullname}
                      </p>
                      <p className="text-sm text-left text-gray-500">
                        {item.attributes.email}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {isOpen &&
                search.length >= config.min_search_length &&
                !staffMembers?.data.length && (
                  <li className="px-4 py-2 w-full cursor-pointer bg-white hover:bg-gray-100 flex justify-start">
                    Nenhum resultado encontrado
                  </li>
                )}
            </ul>
          </div>
        )}
      </Downshift>
    </div>
  )
}
